import { useState, useCallback, useEffect } from "react";
import { ScrollView, RefreshControl, Pressable, View, Text } from "react-native";
import { useRouter } from "expo-router";
import { collection, collectionGroup, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import ContentWidth from "@/components/ContentWidth";
import { FinancesSkeleton } from "@/components/Skeleton";
import { colors, radius, font } from "@/lib/theme";
import type { Fee, Member } from "@/lib/types";
import styled from "styled-components/native";

/* ── styled ────────────────────────────────────────── */

const Section = styled.View`
  padding: 20px 20px 0;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const HeroCard = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.lg}px;
  padding: 20px;
  margin: 20px 20px 0;
`;

const HeroLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: rgba(255, 255, 255, 0.75);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const HeroAmount = styled.Text`
  font-size: 38px;
  font-weight: ${font.extrabold};
  color: #fff;
  letter-spacing: -1px;
  margin-top: 4px;
`;

const HeroSub = styled.Text`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: ${font.semibold};
  margin-top: 6px;
`;

const StatRow = styled.View`
  flex-direction: row;
  gap: 10px;
  margin: 12px 20px 0;
`;

const StatCard = styled.View`
  flex: 1;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 14px;
`;

const StatLabel = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.Text<{ color?: string }>`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${(p) => p.color || colors.ink};
  margin-top: 4px;
  letter-spacing: -0.5px;
`;

const Card = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ hasBorder?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  ${(p) => p.hasBorder && `border-bottom-width: 1px; border-bottom-color: ${colors.line};`}
`;

const Avatar = styled.View`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: ${colors.amber50};
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.amber200};
`;

const AvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const RowMain = styled.View`
  flex: 1;
  margin-left: 12px;
`;

const RowName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const RowSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 2px;
`;

const RowAmount = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.amber};
  margin-right: 10px;
`;

const RemindBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding: 6px 12px;
`;

const RemindBtnText = styled.Text`
  color: #fff;
  font-size: 11px;
  font-weight: ${font.bold};
`;

const SeeAllBtn = styled.View`
  padding: 14px 16px;
  align-items: center;
`;

const SeeAllText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

const ChartCard = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
`;

const ChartTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ChartSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-bottom: 16px;
`;

const EmptyText = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 20px;
`;

/* ── helpers ───────────────────────────────────────── */

function pence(p: number): string {
  return `\u00A3${(p / 100).toFixed(0)}`;
}

function startOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function getLast8WeeksRanges(): { label: string; start: string; end: string }[] {
  const weeks: { label: string; start: string; end: string }[] = [];
  const now = new Date();
  for (let i = 7; i >= 0; i--) {
    const end = new Date(now);
    end.setDate(now.getDate() - i * 7);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    weeks.push({
      label: end.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      start: fmt(start),
      end: fmt(end),
    });
  }
  return weeks;
}

interface OutstandingPlayer {
  userId: string;
  displayName: string;
  email: string;
  unpaidFees: number;
  unpaidCount: number;
  unpaidTraining: number;
  unpaidTrainingCount: number;
  membershipOwed: number;
  total: number;
}

/* ── component ─────────────────────────────────────── */

export default function FinancesScreen() {
  const { club } = useClub();
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allFees, setAllFees] = useState<(Fee & { id: string; matchId: string })[]>([]);
  const [members, setMembers] = useState<(Member & { id: string })[]>([]);

  const loadData = useCallback(async () => {
    if (!club) return;
    try {
      const feeQuery = query(
        collectionGroup(db, "fees"),
        where("clubId", "==", club.id)
      );
      const feeSnap = await getDocs(feeQuery);
      const fees = feeSnap.docs.map((d) => ({
        id: d.id,
        matchId: d.ref.parent.parent?.id || "",
        ...d.data(),
      })) as (Fee & { id: string; matchId: string })[];

      const memberSnap = await getDocs(collection(db, "clubs", club.id, "members"));
      const allMembers = memberSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as (Member & { id: string })[];

      setAllFees(fees);
      setMembers(allMembers);
    } catch (err) {
      console.error("Failed to load finances:", err);
    }
  }, [club?.id]);

  // Load once on mount
  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <FinancesSkeleton />
      </View>
    );
  }

  /* ── compute totals ─────────────────────────── */

  const yearStart = startOfYear();

  // This season (calendar year) — split match fees vs training fees
  const seasonFees = allFees.filter((f) => f.matchDate >= yearStart);
  const matchFees = seasonFees.filter((f) => (f.feeType ?? "match") === "match");
  const trainingFees = seasonFees.filter((f) => f.feeType === "training");
  const collectedThisSeason = matchFees.filter((f) => f.paid).reduce((s, f) => s + f.amount, 0);
  const outstandingFees = matchFees.filter((f) => !f.paid).reduce((s, f) => s + f.amount, 0);
  const trainingCollected = trainingFees.filter((f) => f.paid).reduce((s, f) => s + f.amount, 0);
  const trainingOutstanding = trainingFees.filter((f) => !f.paid).reduce((s, f) => s + f.amount, 0);
  const hasTrainings = trainingFees.length > 0;

  // Membership
  const membersWithMembership = members.filter((m) => m.membership);
  let membershipCollected = 0;
  let membershipOutstanding = 0;
  let membershipFullyPaid = 0;
  let membershipExpected = 0;
  for (const m of members) {
    if (!m.membership) continue;
    membershipCollected += m.membership.paidAmount;
    membershipExpected += m.membership.totalAmount;
    const remaining = m.membership.totalAmount - m.membership.paidAmount;
    if (remaining > 0) membershipOutstanding += remaining;
    else membershipFullyPaid++;
  }

  const totalCollected = collectedThisSeason + trainingCollected + membershipCollected;
  const totalOutstanding = outstandingFees + trainingOutstanding + membershipOutstanding;

  // Per-player outstanding
  const playerMap = new Map<string, OutstandingPlayer>();
  for (const m of members) {
    playerMap.set(m.userId, {
      userId: m.userId,
      displayName: m.displayName,
      email: m.email,
      unpaidFees: 0,
      unpaidCount: 0,
      unpaidTraining: 0,
      unpaidTrainingCount: 0,
      membershipOwed: m.membership ? Math.max(0, m.membership.totalAmount - m.membership.paidAmount) : 0,
      total: 0,
    });
  }
  for (const f of seasonFees) {
    if (f.paid) continue;
    const p = playerMap.get(f.userId);
    if (!p) continue;
    if ((f.feeType ?? "match") === "match") {
      p.unpaidFees += f.amount;
      p.unpaidCount += 1;
    } else if (f.feeType === "training") {
      p.unpaidTraining += f.amount;
      p.unpaidTrainingCount += 1;
    }
  }
  const outstandingPlayers = Array.from(playerMap.values())
    .map((p) => ({ ...p, total: p.unpaidFees + p.unpaidTraining + p.membershipOwed }))
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total);

  const topDefaulters = outstandingPlayers.slice(0, 5);

  // Last 8 weeks income chart
  const weeks = getLast8WeeksRanges();
  const weeklyIncome = weeks.map((w) => {
    const total = allFees
      .filter((f) => f.paid && f.matchDate >= w.start && f.matchDate <= w.end)
      .reduce((s, f) => s + f.amount, 0);
    return { ...w, total };
  });
  const maxWeekly = Math.max(...weeklyIncome.map((w) => w.total), 1);

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.brand} />}
    >
      <ContentWidth>
        {/* Hero — total collected this season */}
        <HeroCard>
          <HeroLabel>Collected this season</HeroLabel>
          <HeroAmount>{pence(totalCollected)}</HeroAmount>
          <HeroSub>
            {totalOutstanding > 0
              ? `${pence(totalOutstanding)} outstanding`
              : "All caught up 🎉"}
          </HeroSub>
        </HeroCard>

        {/* Stat row */}
        <StatRow>
          <StatCard>
            <StatLabel>Match fees</StatLabel>
            <StatValue color={colors.brand}>{pence(collectedThisSeason)}</StatValue>
            {outstandingFees > 0 && (
              <Text style={{ fontSize: 11, color: colors.amber, fontWeight: "600", marginTop: 2 }}>
                {pence(outstandingFees)} owed
              </Text>
            )}
          </StatCard>
          {hasTrainings && (
            <StatCard>
              <StatLabel>Training</StatLabel>
              <StatValue color={colors.brand}>{pence(trainingCollected)}</StatValue>
              {trainingOutstanding > 0 && (
                <Text style={{ fontSize: 11, color: colors.amber, fontWeight: "600", marginTop: 2 }}>
                  {pence(trainingOutstanding)} owed
                </Text>
              )}
            </StatCard>
          )}
          <StatCard>
            <StatLabel>Membership</StatLabel>
            <StatValue color={colors.brand}>{pence(membershipExpected)}</StatValue>
            {membershipOutstanding > 0 ? (
              <Text style={{ fontSize: 11, color: colors.amber, fontWeight: "600", marginTop: 2 }}>
                {pence(membershipOutstanding)} owed
              </Text>
            ) : membersWithMembership.length > 0 ? (
              <Text style={{ fontSize: 11, color: colors.inkMuted, fontWeight: "600", marginTop: 2 }}>
                all paid
              </Text>
            ) : null}
          </StatCard>
        </StatRow>

        {/* Income chart */}
        <Section>
          <SectionLabel>Income — last 8 weeks</SectionLabel>
        </Section>
        <ChartCard>
          <ChartTitle>{pence(weeklyIncome.reduce((s, w) => s + w.total, 0))}</ChartTitle>
          <ChartSub>Total collected over the last 8 weeks</ChartSub>
          <View style={{ flexDirection: "row", alignItems: "flex-end", height: 100, gap: 6 }}>
            {weeklyIncome.map((w, i) => {
              const heightPct = (w.total / maxWeekly) * 100;
              return (
                <View key={i} style={{ flex: 1, alignItems: "center" }}>
                  <View style={{
                    width: "100%",
                    height: `${Math.max(heightPct, 4)}%`,
                    backgroundColor: w.total > 0 ? colors.brand : colors.line,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }} />
                </View>
              );
            })}
          </View>
          <View style={{ flexDirection: "row", marginTop: 8, gap: 6 }}>
            {weeklyIncome.map((w, i) => (
              <Text key={i} style={{ flex: 1, fontSize: 9, color: colors.inkMuted, textAlign: "center", fontWeight: "600" }}>
                {w.label}
              </Text>
            ))}
          </View>
        </ChartCard>

        {/* Top defaulters */}
        <Section>
          <SectionLabel>Who owes</SectionLabel>
        </Section>
        {topDefaulters.length === 0 ? (
          <Card>
            <EmptyText>🎉 Everyone's all paid up</EmptyText>
          </Card>
        ) : (
          <Card>
            {topDefaulters.map((p, i) => {
              const isLast = i === topDefaulters.length - 1 && outstandingPlayers.length <= 5;
              return (
                <Pressable
                  key={p.userId}
                  onPress={() => router.push(`/(app)/member-detail?userId=${p.userId}`)}
                  style={({ pressed }) => [pressed && { opacity: 0.7 }]}
                >
                  <Row hasBorder={!isLast}>
                    <Avatar>
                      <AvatarText>{p.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
                    </Avatar>
                    <RowMain>
                      <RowName>{p.displayName}</RowName>
                      <RowSub>
                        {[
                          p.unpaidCount > 0 && `${p.unpaidCount} match fee${p.unpaidCount > 1 ? "s" : ""}`,
                          p.unpaidTrainingCount > 0 && `${p.unpaidTrainingCount} training`,
                          p.membershipOwed > 0 && "Membership",
                        ].filter(Boolean).join(" · ")}
                      </RowSub>
                    </RowMain>
                    <RowAmount>{pence(p.total)}</RowAmount>
                    <Text style={{ fontSize: 18, color: colors.inkMuted }}>›</Text>
                  </Row>
                </Pressable>
              );
            })}
            {outstandingPlayers.length > 5 && (
              <Pressable
                onPress={() => router.push("/(app)/finances-outstanding")}
                style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1, borderTopWidth: 1, borderTopColor: colors.line })}
              >
                <SeeAllBtn>
                  <SeeAllText>See all {outstandingPlayers.length} outstanding</SeeAllText>
                </SeeAllBtn>
              </Pressable>
            )}
          </Card>
        )}

        <View style={{ height: 40 }} />
      </ContentWidth>
    </ScrollView>
  );
}
